/**
 * Where every tile goes — as numbers, not as DOM.
 *
 * The grid used to express its layout as nested flexbox: a column of row
 * `<div>`s, each holding its tiles. That made a tile's position part of the
 * component tree, so any change in how many tiles a row holds (3 people
 * becoming 4 turns one row of three into two rows of two) moved a tile to a
 * different parent — and React cannot move an element between parents, it
 * unmounts and remounts it. For a tile that meant a new `<video>`, a blank
 * frame and a fade back in, on someone who had not changed at all.
 *
 * Everything here is pure: row distribution, tile sizes and the final
 * rectangle of every tile. Renderers place tiles from these rectangles as
 * siblings of one container, so a tile keeps its element for as long as it is
 * on screen and only its coordinates change.
 *
 * The maths is the grid's existing algorithm, moved here unchanged — the
 * rectangles reproduce exactly what the flexbox rows used to draw.
 */

/** Every tile is drawn at the camera's shape. */
export const TILE_ASPECT = 16 / 9
const ASPECT = TILE_ASPECT

/** A tile's box inside its container, in CSS pixels. */
export type TileRect = { x: number; y: number; w: number; h: number }

// Responsive gap - smaller on mobile for better space utilization
export const getGap = (containerWidth: number) => {
  if (containerWidth < 400) return 4  // Very small phones: minimal gap
  if (containerWidth < 640) return 6  // Mobile: smaller gap
  if (containerWidth < 1024) return 8  // Tablet: medium gap
  return 10  // Desktop: standard gap
}

export function computeLegacyLayout(
  count: number,
  containerW: number,
  containerH: number,
): { cols: number; rows: number; tileW: number; tileH: number } {
  if (count === 0 || containerW === 0 || containerH === 0) {
    return { cols: 1, rows: 1, tileW: containerW, tileH: containerH }
  }

  const GAP = getGap(containerW)

  // ── Tablet portrait override for 4 participants ─────────────────────
  // Force 2x2 grid (2 columns) instead of single column
  const isTabletPortrait = containerW >= 640 && containerW < 1024 && containerH > containerW
  if (isTabletPortrait && count === 4) {
    const cols = 2
    const rows = 2
    // Fit the cell to the video's shape, exactly as the search below does.
    // Dividing the container up and using the result verbatim made a cell
    // that is not 16:9, so the frame inside it could not both fill it and
    // stay uncropped.
    const availW = (containerW - GAP) / cols
    const availH = (containerH - GAP) / rows
    let tileW = availW
    let tileH = tileW / ASPECT
    if (tileH > availH) {
      tileH = availH
      tileW = tileH * ASPECT
    }
    return { cols, rows, tileW, tileH }
  }

  let bestCols = 1
  let bestTileW = 0
  let bestTileH = 0

  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols)
    const availW = (containerW - GAP * (cols - 1)) / cols
    const availH = (containerH - GAP * (rows - 1)) / rows

    let tileW = availW
    let tileH = tileW / ASPECT
    if (tileH > availH) {
      tileH = availH
      tileW = tileH * ASPECT
    }

    if (tileW > bestTileW) {
      bestTileW = tileW
      bestTileH = tileH
      bestCols = cols
    }
  }

  return {
    cols: bestCols,
    rows: Math.ceil(count / bestCols),
    tileW: bestTileW,
    tileH: bestTileH,
  }
}

/**
 * Google Meet–quality layout algorithm.
 *
 * Key principles:
 * 1. Every row spans the FULL container width (tiles in each row are sized
 *    proportionally so the row fills 100 %).
 * 2. All rows have the same height = (containerH - gaps) / numRows.
 * 3. Scoring picks the row-count that gives the best 16:9 fit per tile.
 * 4. Explicit overrides for mobile portrait, mobile/tablet landscape,
 *    tablet portrait, and desktop — matching the user's spec exactly.
 */
export function computeRowDistribution(count: number, containerW: number, containerH: number): number[] {
  if (count === 0 || containerW === 0 || containerH === 0) return [1]

  const GAP = getGap(containerW)
  const containerAspect = containerW / containerH

  // ── Helpers ──────────────────────────────────────────────────────────
  /** Score a candidate row-count by how close the avg tile ratio is to 16:9 */
  const scoreRows = (numRows: number) => {
    const rowH = (containerH - GAP * (numRows - 1)) / numRows
    const avgCols = count / numRows
    const tileW = (containerW - GAP * (avgCols - 1)) / avgCols
    const ratio = tileW / rowH
    return (ratio > ASPECT ? ASPECT / ratio : ratio / ASPECT) * tileW * rowH
  }

  /** Build the distribution array once we know bestRows */
  const distribute = (bestRows: number): number[] => {
    const base = Math.floor(count / bestRows)
    const extra = count % bestRows
    // Put the bigger rows (base+1) at the END so the grid looks bottom-heavy
    const rows: number[] = []
    for (let r = 0; r < bestRows; r++) {
      rows.push(r < bestRows - extra ? base : base + 1)
    }
    return rows
  }

  // ── Device / orientation classification ──────────────────────────────
  const isMobilePortrait = containerW < 640 && containerH > containerW
  const isMobileLandscape = containerW < 640 && containerW > containerH
  // Landscape detection that includes tablets (iPad landscape ~1024×768)
  const isLandscape = containerW > containerH && containerW < 1280
  // Tablet portrait: 640-1024 width, taller than wide
  const isTabletPortrait = containerW >= 640 && containerW < 1024 && containerH > containerW
  // Desktop: width >= 1024 OR landscape >= 1280
  const isDesktop = containerW >= 1024 && !isTabletPortrait

  // ── 1. MOBILE PORTRAIT ───────────────────────────────────────────────
  if (isMobilePortrait) {
    // 1-column stack for ≤4, 2-column for 5+
    if (count <= 4) return distribute(count)
    return distribute(Math.ceil(count / 2))
  }

  // ── 2. MOBILE / TABLET LANDSCAPE ────────────────────────────────────
  if (isMobileLandscape || (isLandscape && !isDesktop)) {
    if (count <= 3) return distribute(1)          // single row
    if (count === 4) return distribute(2)          // 2×2
    if (count <= 6) return distribute(2)           // 2 rows (2+3 or 3+3)
    return distribute(Math.ceil(count / 3))        // 3-col
  }

  // ── 3. TABLET PORTRAIT ──────────────────────────────────────────────
  //    User-specified layouts for the narrow, tall viewport.
  //    Rules: fill width, avoid big empty rectangles.
  if (isTabletPortrait) {
    if (count === 1) return [1]                    // full screen
    if (count === 2) return [1, 1]                 // 2 stacked full-width
    if (count === 3) return [2, 1]                 // 2 top + 1 full-width bottom
    if (count === 4) return [2, 2]                 // 2×2
    if (count === 5) return [2, 2, 1]              // 2+2+1 (last centered, not full-width)
    if (count === 6) return [2, 2, 2]              // 3 rows of 2
    if (count === 7) return [3, 3, 1]              // 3+3+1
    if (count === 8) return [3, 3, 2]              // 3+3+2
    if (count === 9) return [3, 3, 3]              // 3×3
    // 10+: use 3-column layout
    return distribute(Math.ceil(count / 3))
  }

  // ── 4. DESKTOP (and wide viewports) ─────────────────────────────────
  // Generic scoring pass
  let bestRows = 1
  let bestScore = 0
  for (let numRows = 1; numRows <= count; numRows++) {
    const s = scoreRows(numRows)
    if (s > bestScore) { bestScore = s; bestRows = numRows }
  }

  // Meet-style overrides for common counts
  if (count === 2) bestRows = containerW < 520 ? 2 : 1
  if (count === 3) {
    if (containerW < 500) bestRows = 3
    else if (containerW < 1000) bestRows = 2
    else bestRows = 1
  }
  if (count === 4) bestRows = scoreRows(1) >= scoreRows(2) ? 1 : 2
  if (count === 6) bestRows = containerW < 900 ? 3 : 2
  if (count === 7) bestRows = containerW < 700 ? bestRows : 2
  if (containerAspect >= 1.1) {
    if (count === 8) bestRows = 2
    if (count === 9) bestRows = containerW < 1250 ? 3 : 2
  }

  return distribute(bestRows)
}

type RowsLayoutOptions = {
  /** Container width. */
  width: number
  /** Container height, padding included. */
  height: number
  gap: number
  /** Height of every row. Tiles are centred vertically inside it. */
  rowHeight: number
  /** Width of each tile in row `rowIndex`, which holds `count` tiles. */
  tileWidth: (rowIndex: number, count: number) => number
  /**
   * Tiles take the row's full height rather than the video's 16:9 shape.
   * Width is unaffected, so a tile whose width was capped stays 16:9 and one
   * limited by the row's width becomes taller than 16:9 — the video fills it
   * with `object-fit: cover`.
   */
  fillRowHeight?: boolean
  padTop?: number
  padBottom?: number
}

/**
 * Rectangles for tiles laid out as centred rows.
 *
 * Reproduces a flex column with `justify-content: center` holding flex rows
 * with `justify-content: center; align-items: center` — the shape every grid
 * and rail in the meeting uses. Rows are centred as a block (overflowing both
 * edges equally when they do not fit, exactly as flexbox does), each row is
 * centred horizontally, and each tile is centred inside its row's height.
 *
 * Returns one rectangle per tile, in reading order.
 */
export function layoutRows(rowCounts: readonly number[], opts: RowsLayoutOptions): TileRect[] {
  const { width, height, gap, rowHeight, tileWidth, fillRowHeight = false, padTop = 0, padBottom = 0 } = opts
  const rows = rowCounts.filter((n) => n > 0)
  if (rows.length === 0) return []

  const blockHeight = rows.length * rowHeight + gap * (rows.length - 1)
  const available = height - padTop - padBottom
  const top = padTop + (available - blockHeight) / 2

  const rects: TileRect[] = []
  rows.forEach((count, rowIndex) => {
    const w = Math.max(0, tileWidth(rowIndex, count))
    const h = fillRowHeight ? rowHeight : w / ASPECT
    const rowWidth = count * w + gap * (count - 1)
    const left = (width - rowWidth) / 2
    const rowTop = top + rowIndex * (rowHeight + gap)
    const y = rowTop + (rowHeight - h) / 2
    for (let i = 0; i < count; i++) {
      rects.push({ x: left + i * (w + gap), y, w, h })
    }
  })
  return rects
}

/**
 * Every tile's rectangle for the main participant grid.
 *
 * `auto` is the Meet-style row distribution, where each row fills the width
 * and every tile fills its row's height — the dynamic layout as designed. Two
 * people are two tall tiles covering the stage, three are three portrait
 * tiles, and a row's tiles are only held to 16:9 when they would otherwise be
 * wider than that. `legacy` is the tiled view's uniform 16:9 cells. `count`
 * includes the "+N" group tile when there is one — it takes a cell like anyone
 * else.
 */
export function computeGridRects(
  count: number,
  width: number,
  height: number,
  variant: 'auto' | 'legacy',
): TileRect[] {
  if (count <= 0 || width <= 0 || height <= 0) return []
  const gap = getGap(width)

  if (variant === 'legacy') {
    const { cols, rows, tileW, tileH } = computeLegacyLayout(count, width, height)
    const rowCounts: number[] = []
    for (let r = 0; r < rows; r++) rowCounts.push(Math.min(cols, count - r * cols))
    return layoutRows(rowCounts, {
      width,
      height,
      gap,
      rowHeight: tileH,
      tileWidth: () => tileW,
    })
  }

  const rowCounts = computeRowDistribution(count, width, height)
  const rowHeight = (height - gap * (rowCounts.length - 1)) / rowCounts.length
  return layoutRows(rowCounts, {
    width,
    height,
    gap,
    rowHeight,
    // Each row fills the width, capped at 16:9 for the row's height so a lone
    // tile in a wide row does not stretch into a letterbox.
    tileWidth: (_row, n) => Math.min((width - gap * (n - 1)) / n, rowHeight * ASPECT),
    fillRowHeight: true,
  })
}
